import * as React from 'react';

import {
  enumPrinterType,
  type PrinterPageSize,
  type PrinterRollWidth,
  type PrinterType,
} from '@rematter/frontend-lib-api-client';
import {
  ContentSection,
  LocalizedDate,
  Select,
  TextField,
} from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';

import {
  $printerForm,
  PrinterFormState,
} from '../../../_state/printerForm.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { PrinterDetailRecord } from '../../../types/sidecar';

type PrinterFormFieldsProps = {
  printer?: PrinterDetailRecord | null;
  printerId?: string;
  className?: string;
};

const printerFormSelector = (state: PrinterFormState) => ({
  name: state.name,
  printerType: state.printerType,
  pageSize: state.pageSize,
  rollWidth: state.rollWidth,
});

export const PrinterFormFields = ({
  printer,
  printerId,
  className,
}: PrinterFormFieldsProps) => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  const selectedFormAtom = React.useMemo(
    () => selectAtom($printerForm, printerFormSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);
  const updateForm = useSetAtom($printerForm);

  const handleFieldChange = React.useCallback(
    <K extends keyof PrinterFormState>(field: K) =>
      (value: PrinterFormState[K]) => {
        updateForm((prev) => ({
          ...prev,
          [field]: value,
        }));
      },
    [updateForm],
  );

  const handlePrinterTypeChange = React.useCallback(
    (value: PrinterType | null) => {
      if (!value) return;
      updateForm((prev) => ({
        ...prev,
        printerType: value,
        pageSize: value === enumPrinterType.Standard ? prev.pageSize : null,
        rollWidth: value === enumPrinterType.Thermal ? prev.rollWidth : null,
      }));
    },
    [updateForm],
  );

  const printerTypeOptions = React.useMemo(
    () =>
      Object.values(enumPrinterType).map((v) => ({
        key: v,
        label:
          t.hardwareView.printerDetailView.printerTypeLabels[
            v as keyof typeof t.hardwareView.printerDetailView.printerTypeLabels
          ],
      })),
    [t],
  );

  const pageSizeOptions = React.useMemo(
    () =>
      (['Letter', 'A4', 'Legal'] as const).map((v) => ({
        key: v,
        label:
          t.hardwareView.printerDetailView.pageSizeLabels[
            v as keyof typeof t.hardwareView.printerDetailView.pageSizeLabels
          ],
      })),
    [t],
  );

  const rollWidthOptions = React.useMemo(
    () =>
      (['Roll58mm', 'Roll80mm'] as const).map((v) => ({
        key: v,
        label:
          t.hardwareView.printerDetailView.rollWidthLabels[
            v as keyof typeof t.hardwareView.printerDetailView.rollWidthLabels
          ],
      })),
    [t],
  );

  const statusLabel =
    t.hardwareView.printersPanel.statusLabels[
      printer?.status as keyof typeof t.hardwareView.printersPanel.statusLabels
    ] ?? printer?.status;

  return (
    <div className={cn('@PrinterFormFields', className)}>
      <ContentSection
        title={t.hardwareView.printerDetailView.sections.configuration}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <TextField
            label={t.hardwareView.printerDetailView.fields.name}
            value={form.name}
            onChange={(value) => handleFieldChange('name')(value)}
            isRequired
          />
          <Select
            label={t.hardwareView.printerDetailView.fields.printerType}
            value={form.printerType}
            onChange={handlePrinterTypeChange}
            options={printerTypeOptions}
          />
          {form.printerType === enumPrinterType.Standard && (
            <Select
              label={t.hardwareView.printerDetailView.fields.pageSize}
              value={form.pageSize}
              onChange={(v) =>
                handleFieldChange('pageSize')(v as PrinterPageSize | null)
              }
              options={pageSizeOptions}
            />
          )}
          {form.printerType === enumPrinterType.Thermal && (
            <Select
              label={t.hardwareView.printerDetailView.fields.rollWidth}
              value={form.rollWidth}
              onChange={(v) =>
                handleFieldChange('rollWidth')(v as PrinterRollWidth | null)
              }
              options={rollWidthOptions}
            />
          )}
        </div>
      </ContentSection>

      <ContentSection
        title={t.hardwareView.printerDetailView.sections.deviceInfo}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          {printerId && (
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral/60">
                {t.hardwareView.printerDetailView.fields.printerId}
              </span>
              <span className="font-mono text-sm">{printerId}</span>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.printerDetailView.fields.uniqueId}
            </span>
            <span className="font-mono text-sm">
              {printer?.uniqueId ?? '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.printerDetailView.fields.driverType}
            </span>
            <span className="text-sm font-medium">
              {printer?.driverType ?? '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.printerDetailView.fields.manufacturer}
            </span>
            <span className="text-sm font-medium">
              {printer?.manufacturer ?? '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.printerDetailView.fields.model}
            </span>
            <span className="text-sm font-medium">
              {printer?.model ?? '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.printerDetailView.fields.firmwareVersion}
            </span>
            <span className="text-sm font-medium">
              {printer?.firmwareVersion ?? '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.printerDetailView.fields.status}
            </span>
            <span className="text-sm font-medium">
              {statusLabel ?? '\u2013'}
            </span>
          </div>
        </div>
      </ContentSection>

      <ContentSection
        title={t.hardwareView.printerDetailView.sections.connection}
        variant="panel"
      >
        <div className="flex flex-col gap-1">
          <span className="text-sm text-neutral/60">
            {t.hardwareView.printerDetailView.fields.sidecar}
          </span>
          {printer?.sidecar ? (
            <button
              className="w-fit text-sm font-medium text-primary hover:underline"
              onClick={() =>
                setLocation(`/hardware/sidecars/${printer.sidecar?.id}`)
              }
            >
              {printer.sidecar.name}
            </button>
          ) : (
            <span className="text-sm font-medium">{'\u2013'}</span>
          )}
        </div>
      </ContentSection>

      <ContentSection
        title={t.hardwareView.printerDetailView.sections.timestamps}
        variant="panel"
        hideDivider
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.printerDetailView.fields.created}
            </span>
            <span className="text-sm font-medium">
              {printer?.timestamps.createdAt ? (
                <LocalizedDate date={printer.timestamps.createdAt} />
              ) : (
                '-'
              )}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.printerDetailView.fields.updated}
            </span>
            <span className="text-sm font-medium">
              {printer?.timestamps.updatedAt ? (
                <LocalizedDate date={printer.timestamps.updatedAt} />
              ) : (
                '-'
              )}
            </span>
          </div>
        </div>
      </ContentSection>
    </div>
  );
};
